import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Breadcrumbs,
  CardComponent,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HOME_SCREEN, TERM_COMPILER } from '../../core-utils/routes';
import Loader from '../../_components/molecules/Loader';
import CategoryTable from '../../_components/atoms/CategoryTable';
import {
  TermCompilerProvider,
  useTermCompilerContext,
} from './TermCompilerContext';
import { CheckBox, CircularLoader } from '@HomaHealth/ui-component-library';
import {
  formatCategoryTableData,
  formatColumns,
  formatNullTableData,
  nullDataColumns,
  SelectedTermsDataType,
  SelectedTopicDataType,
  TermsDataType,
} from '../../core-utils/helper-utils/termCompilerHelper';
import AutocompleteWithCheckBoxes from '../../_components/atoms/Autocomplete';
import { getTextWithToolTip } from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import PdfHighlighter from '../../_components/organisms/PdfHighlighter';
import { createBlobUrl } from '../../core-utils/helper-utils/contractAIQHelper';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';

const InformationExtracted: React.FC<{}> = () => {
  const {
    tableLoading,
    termCompilerData,
    filters,
    setFilters,
    openDocument,
    setOpenDocument,
    documentDetails,
    setDocumentDetails,
    documents,
    setDocuments,
  } = useTermCompilerContext();

  const { classes } = makeStyles()({
    main: {
      width: '1442px',
      padding: '15px 20px 0px 20px',
      display: 'flex',
      height: '563px',
    },
    leftView: {
      width: openDocument ? '55%' : '100%',
      overflow: 'auto',
      ...scrollBarStyles,
    },
    rightView: {
      width: '45%',
    },
    parameterBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    tooltip: {
      backgroundColor: patientAccessTheme.palette.grey[900],
      padding: '10px',
      fontSize: '13px',
      fontFamily: 'Inter',
      fontWeight: 400,
      lineHeight: '16px',
      color: patientAccessTheme.palette.status.white,
    },
    wideTooltip: {
      maxWidth: '800px',
    },
    arrow: {
      color: patientAccessTheme.palette.grey[900],
      marginLeft: '50px',
    },
    popper: {
      marginTop: '-8px',
      marginLeft: '-34px',
    },
    ellipsis: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      color: patientAccessTheme.palette.text.mediumemphasis,
      '&:hover': {
        overflow: 'visible',
        whiteSpace: 'normal',
        height: 'auto',
      },
    },
    underline: {
      '&:hover': {
        color: patientAccessTheme.palette.primary.main,
        textDecoration: 'underline dashed',
        textUnderlineOffset: '0.25rem',
        cursor: 'pointer',
      },
    },
  })();

  const handleFilterChange = (topic: string, selectedNames: string[]) => {
    setFilters((prev) => ({
      ...prev,
      [topic]: selectedNames, // replace for that section
    }));
  };

  const onClickReference = async (
    source: string,
    chunks: any[],
    pageNumber: number,
  ) => {
    const selectedDoc = documents.find(
      (temp: any) => temp.document_name === source,
    );
    let documentUrl = selectedDoc?.blobUrl ?? null;
    if (!documentUrl) {
      documentUrl = await createBlobUrl(selectedDoc?.document_url);
      setDocuments((prevDocs: any[]) =>
        prevDocs.map((doc) =>
          doc.document_name === source ? { ...doc, blobUrl: documentUrl } : doc,
        ),
      );
    }
    setDocumentDetails({ documentUrl, chunks, pageNumber });
    setOpenDocument(true);
  };

  return (
    <Grid className={classes.main}>
      <Grid className={classes.leftView}>
        {tableLoading ? (
          <CircularLoader />
        ) : (
          <>
            {termCompilerData?.selected_terms?.map(
              (temp: SelectedTopicDataType) => {
                const selectedTermsForThisTopic = filters[temp.topic] || [];
                const isTermsDataAvailable = temp.term_topics.some(
                  (item: any) =>
                     
                    item.terms.some((t: any) => t.value !== null),
                );
                return (
                  isTermsDataAvailable && (
                    <CardComponent
                      key={temp.topic}
                      title={temp.label}
                      topRightItem={
                        <AutocompleteWithCheckBoxes
                          placeholder={'Filter By Terms'}
                          width="310px"
                          options={temp.term_topics.flatMap((t) =>
                             
                            t.terms.map((term) => term.term),
                          )}
                          handleChange={(event, selectedNames) =>
                            handleFilterChange(temp.topic, selectedNames)
                          }
                          getOptionLabel={(option: any) => option}
                          renderOption={(option: any, { selected }: any) => {
                            return (
                              <Box
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <CheckBox
                                  checked={selected}
                                  id=""
                                  name=""
                                  color="primary"
                                />
                                {getTextWithToolTip(option, 25)}
                              </Box>
                            );
                          }}
                        />
                      }
                      mainGridStyles={{ marginBottom: '20px' }}
                      body={
                        <Grid
                          style={{
                            borderTop: `solid 1px ${patientAccessTheme.palette.grey[300]}`,
                            paddingTop: '20px',
                            marginTop: '10px',
                          }}
                        >
                          <CategoryTable
                            columns={formatColumns(temp.label)}
                            rowsData={formatCategoryTableData(
                              temp.term_topics,
                              classes,
                              selectedTermsForThisTopic,
                              onClickReference,
                            )}
                            isCategory
                          />
                        </Grid>
                      }
                    />
                  )
                );
              },
            )}
            <CardComponent
              title={'Missing Terms Summary'}
              mainGridStyles={{ marginBottom: '20px', marginTop: '30px' }}
              body={
                <Grid
                  style={{
                    borderTop: `solid 1px ${patientAccessTheme.palette.grey[300]}`,
                    paddingTop: '20px',
                    marginTop: '10px',
                  }}
                >
                  <CategoryTable
                    columns={nullDataColumns}
                    rowsData={formatNullTableData(
                      termCompilerData.selected_terms,
                      classes,
                    )}
                    isCategory
                  />
                </Grid>
              }
            />
          </>
        )}
      </Grid>
      {openDocument && (
        <Grid className={classes.rightView}>
          <PdfHighlighter
            key={`${documentDetails?.documentUrl}-${documentDetails?.pageNumber}-${documentDetails?.chunks?.length}`}
            pdfUrl={documentDetails?.documentUrl}
            chunks={documentDetails?.chunks}
            highlightIds={documentDetails?.chunks.map(
              (item: any) => item.chunk_id,
            )}
            pageNumber={documentDetails?.pageNumber}
            contentStyles={{ height: '563px' }}
            onClose={() => setOpenDocument(false)}
          />
        </Grid>
      )}
    </Grid>
  );
};

const PageContents: React.FC<{}> = () => {
  const { classes } = makeStyles()({
    breadcrumbs: {
      padding: '12px 20px',
      borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      width: '100%',
    },
    main: {
      width: '1442px',
      padding: '15px 20px 0px 20px',
    },
    hr: {
      width: '100%',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    infoContainer: {
      marginTop: '1rem',
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    info: {
      display: 'flex',
      flexDirection: 'column',
    },
  })();

  const navigate = useNavigate();
  const { loading, termCompilerData } = useTermCompilerContext();

  const breadcrumbsItemsList = [
    [
      'text',
      'Home',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(HOME_SCREEN);
      },
    ],
    ,
    [
      'text',
      'Term Compiler',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(TERM_COMPILER);
      },
    ],
    [
      'text',
      `${termCompilerData?.compiler_name}`,
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const handleExportCSV = () => {
    let csv = 'Type,Topic,Term,Description,Answer,Evidence\n';

    termCompilerData.selected_terms.forEach((topic: SelectedTopicDataType) => {
      topic.term_topics.forEach((termTopic: SelectedTermsDataType) => {
         
        termTopic.terms.forEach((term: TermsDataType) => {
          // Flatten evidence while preserving relationships
          const allEvidence: string[] = [];

           
          term.extractions.forEach((extraction) => {
             
            extraction.evidence.forEach((ev) => {
              allEvidence.push(
                `<b>Section:</b> ${ev.document_section} | Source: ${extraction.source} | Excerpt: ${ev.excerpt} `,
              );
            });
          });

          csv += `"${topic.topic}","${termTopic.topic}","${term.term}","${
            term.description
          }","${term.value}","${allEvidence.join(' || ')}"\n`;
        });
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${termCompilerData?.compiler_name || 'export'}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <Loader />;
  }
  return (
    <Grid container>
      <Grid className={classes.breadcrumbs}>
        <Breadcrumbs itemList={breadcrumbsItemsList} />
      </Grid>
      <Grid className={classes.main}>
        <PageTitleAndSubTitle
          title={`${termCompilerData.compiler_name} Term Compilation`}
          subTitle={''}
          customButton
          customButtonText="Download Table"
          customButtonVariant="contained"
          customButtonClick={handleExportCSV}
          customButtonWidth="auto"
        />
      </Grid>
      <InformationExtracted />
    </Grid>
  );
};

const TermCompilerInternalPage: React.FC<{}> = () => {
  return (
    <TermCompilerProvider>
      <PageContents />
    </TermCompilerProvider>
  );
};

export default TermCompilerInternalPage;
